// Modified TenantIssues.jsx - Using service providers for staff
import React, {useState, useEffect} from 'react';
import {
    Box, Typography, Alert, Button, Tabs, Tab, CircularProgress, Drawer,
    IconButton, Divider, List, ListItem, ListItemIcon, ListItemText, Snackbar
} from '@mui/material';
import CryptoJS from "crypto-js";
import {ApiDetails, KEY} from "../../dummyData";
import ErrorBoundary from '../../ErrorBoundary';

// Component imports
import TicketDashboard from './TicketDashboard';
import TicketAnalytics from './TicketAnalytics';
import TicketList from './TicketList';
import TicketDetailDialog from './TicketDetailDialog';
import TabPanel from './component/TabPanel';
import BulkActionMenu from './BulkActionMenu';
import ScheduleDialog from './ScheduleDialog';
import CreateTicketDialog from './CreateTicketDialog';
import ServiceProviderManagement from './ServiceProviderManagement';

// Import mock data
import {mockTickets, mockStaff} from './mockData';
import {Plus, Settings, X, Users, Clipboard, Tag} from 'react-feather';

const TenantIssues = () => {
    const [tickets, setTickets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [tabValue, setTabValue] = useState(0);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [ticketDialogOpen, setTicketDialogOpen] = useState(false);
    const [alert, setAlert] = useState({show: false, message: '', severity: 'success'});
    const [error, setError] = useState(null);
    const [bulkActionMenuAnchor, setBulkActionMenuAnchor] = useState(null);
    const [selectedTickets, setSelectedTickets] = useState([]);
    const [addScheduleDialogOpen, setAddScheduleDialogOpen] = useState(false);
    const [createTicketDialogOpen, setCreateTicketDialogOpen] = useState(false);
    const [serviceProviders, setServiceProviders] = useState([]);
    const [problemTypes, setProblemTypes] = useState([]);
    const [categories, setCategories] = useState([]);

    // Settings drawer state
    const [settingsDrawerOpen, setSettingsDrawerOpen] = useState(false);
    const [settingsTabValue, setSettingsTabValue] = useState(0);

    // Get landlord ID utility
    const getLandlordId = () => {
        try {
            let myMapDetailsDecrypted = CryptoJS.AES.decrypt(
                localStorage.getItem("myMap"),
                KEY
            );
            let originalText = myMapDetailsDecrypted.toString(CryptoJS.enc.Utf8);
            return JSON.parse(originalText)[0][1];
        } catch (error) {
            console.error("Error getting landlord ID:", error);
            return null;
        }
    };

    // Fetch tickets
    const fetchTickets = async () => {
        try {
            setLoading(true);
            setError(null);

            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                setLoading(false);
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

            if (response.status === 200) {
                let ticketsData = await response.json();
                setTickets(ticketsData);
            } else if (response.status === 404) {
                setTickets([]);
                showAlert("No tickets found", "warning");
            } else {
                throw new Error(`Error: ${response.status}`);
            }

            setLoading(false);
        } catch (error) {
            console.error("Error fetching tickets:", error);
            setLoading(false);
            setError("Failed to load tickets");
            showAlert("Failed to load tickets. Please try again.", "error");
        }
    };

    // Fetch problem types
    const fetchProblemTypes = async () => {
        try {
            const landlordId = getLandlordId();
            if (!landlordId) return;

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/problem-types", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                setProblemTypes(data);
            }
        } catch (error) {
            console.error("Error fetching problem types:", error);
            showAlert("Failed to load problem types", "error");
        }
    };

    // Fetch categories
    const fetchCategories = async () => {
        try {
            const landlordId = getLandlordId();
            if (!landlordId) return;

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/problems/all", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
            showAlert("Failed to load categories", "error");
        }
    };

    // Fetch service providers instead of staff
    const fetchServiceProviders = async () => {
        try {
            const landlordId = getLandlordId();
            if (!landlordId) return;

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/service-providers", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId
                })
            });

            if (response.status === 200) {
                const data = await response.json();
                setServiceProviders(data.length > 0 ? data : mockStaff); // Fallback to mockStaff if empty
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error fetching service providers:", error);
            showAlert("Failed to load service providers, using mock data", "warning");
            setServiceProviders(mockStaff); // Fallback to mockStaff on error
        }
    };

    // Load data on component mount
    useEffect(() => {
        fetchTickets();
        fetchProblemTypes();
        fetchCategories();
        fetchServiceProviders();
    }, []);

    // Bulk update status
    const bulkUpdateStatus = async (status) => {
        if (selectedTickets.length === 0) return;

        try {
            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/bulk/update", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId,
                    ticket_ids: selectedTickets,
                    status: status.toString().toUpperCase()
                })
            });

            if (response.status === 200) {
                showAlert(`Updated ${selectedTickets.length} tickets to ${status}`, "success");
                setSelectedTickets([]);
                closeBulkActionMenu();
                fetchTickets();
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error updating tickets:", error);
            showAlert("Failed to update tickets", "error");
        }
    };

    // Bulk assign staff
    const bulkAssignStaff = async (staffMember) => {
        if (selectedTickets.length === 0) return;

        try {
            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                return;
            }


            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/bulk/update", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId,
                    ticket_ids: selectedTickets,
                    assigned_to: staffMember.provider_id
                })
            });

            if (response.status === 200) {
                showAlert(`Assigned ${selectedTickets.length} tickets to ${staffMember.first_name} ${staffMember.last_name}`, "success");
                setSelectedTickets([]);
                closeBulkActionMenu();
                fetchTickets();
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error assigning tickets:", error);
            showAlert("Failed to assign tickets", "error");
        }
    };

    // Bulk schedule tickets
    const bulkScheduleTickets = async (scheduleDate, assignedStaffId) => {
        if (selectedTickets.length === 0 || !scheduleDate) return;

        try {
            const landlordId = getLandlordId();
            if (!landlordId) {
                showAlert("Unable to get landlord ID", "error");
                return;
            }

            const response = await fetch(ApiDetails + "pegasus/visionary/tickets/api/tickets/bulk/schedule", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify({
                    landlord_id: landlordId,
                    ticket_ids: selectedTickets,
                    staff_id: assignedStaffId,
                    scheduled_start: scheduleDate
                })
            });

            if (response.status === 200) {
                const assignedProvider = serviceProviders.find(p => p.id === assignedStaffId);
                const providerName = assignedProvider ?
                    `${assignedProvider.first_name} ${assignedProvider.last_name}` :
                    "service provider";

                showAlert(`Scheduled ${selectedTickets.length} tickets for ${new Date(scheduleDate).toLocaleDateString()} with ${providerName}`, "success");
                setSelectedTickets([]);
                closeScheduleDialog();
                fetchTickets();
            } else {
                throw new Error(`Error: ${response.status}`);
            }
        } catch (error) {
            console.error("Error scheduling tickets:", error);
            showAlert("Failed to schedule tickets", "error");
        }
    };

    // Handle tab change
    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    // Handle settings tab change
    const handleSettingsTabChange = (event, newValue) => {
        setSettingsTabValue(newValue);
    };

    // Open ticket details dialog
    const openTicketDialog = (ticket) => {
        setSelectedTicket(ticket);
        setTicketDialogOpen(true);
    };

    // Close ticket details dialog
    const closeTicketDialog = () => {
        setTicketDialogOpen(false);
        setSelectedTicket(null);
    };

    // Open bulk action menu
    const openBulkActionMenu = (event) => {
        setBulkActionMenuAnchor(event.currentTarget);
    };

    // Close bulk action menu
    const closeBulkActionMenu = () => {
        setBulkActionMenuAnchor(null);
    };

    // Open schedule dialog
    const openScheduleDialog = () => {
        setAddScheduleDialogOpen(true);
    };

    // Close schedule dialog
    const closeScheduleDialog = () => {
        setAddScheduleDialogOpen(false);
    };

    // Toggle settings drawer
    const toggleSettingsDrawer = () => {
        setSettingsDrawerOpen(!settingsDrawerOpen);
    };

    // Handle ticket selection for bulk actions
    const toggleTicketSelection = (ticketId) => {
        setSelectedTickets(prev => {
            if (prev.includes(ticketId)) {
                return prev.filter(id => id !== ticketId);
            } else {
                return [...prev, ticketId];
            }
        });
    };

    // Select or deselect all tickets
    const toggleSelectAll = (ticketsToToggle) => {
        if (selectedTickets.length === ticketsToToggle.length) {
            setSelectedTickets([]);
        } else {
            setSelectedTickets(ticketsToToggle.map(t => t.id));
        }
    };

    // Show alert message
    const showAlert = (message, severity = "success") => {
        setAlert({
            show: true,
            message,
            severity
        });

        // Auto-hide alert after 5 seconds
        setTimeout(() => {
            setAlert(prev => ({...prev, show: false}));
        }, 5000);
    };

    // Update ticket function
    const updateTicket = (updatedTicket) => {
        fetchTickets(); // Refresh tickets after update
        showAlert("Ticket updated successfully", "success");
    };

    // Create ticket handler
    const handleCreateTicket = () => {
        fetchTickets(); // Refresh tickets after creating a new one
    };

    // Handle data updates in settings
    const handleSettingsUpdate = (type) => {
        switch (type) {
            case 'serviceProviders':
                fetchServiceProviders();
                break;
            case 'problemTypes':
                fetchProblemTypes();
                break;
            case 'categories':
                fetchCategories();
                break;
            default:
                fetchProblemTypes();
                fetchCategories();
                fetchServiceProviders();
        }
        showAlert(`${type} updated successfully`, "success");
    };

    return (
        <ErrorBoundary>
            <div style={{flex: 1, pt: 2, overflowY: "auto"}}>
                <Box>
                    {/* Alert for notifications */}
                    <Snackbar
                        open={alert.show}
                        autoHideDuration={5000}
                        onClose={() => setAlert({...alert, show: false})}
                        anchorOrigin={{vertical: 'top', horizontal: 'right'}}
                    >
                        <Alert
                            severity={alert.severity}
                            onClose={() => setAlert({...alert, show: false})}
                        >
                            {alert.message}
                        </Alert>
                    </Snackbar>

                    {/* Error message if present */}
                    {error && (
                        <Alert
                            severity="error"
                            sx={{mb: 2}}
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={() => window.location.reload()}
                                >
                                    Reload
                                </Button>
                            }
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Top action buttons */}
                    <Box sx={{display: 'flex', justifyContent: 'space-between', mb: 3}}>
                        <Typography variant="h5" sx={{fontWeight: 600}}>
                            Ticket Management
                        </Typography>
                        <Box>
                            <Button
                                variant="outlined"
                                startIcon={<Settings size={16}/>}
                                onClick={toggleSettingsDrawer}
                                sx={{mr: 2, borderRadius: '8px'}}
                            >
                                Settings
                            </Button>
                            {/* <Button
                                variant="contained"
                                color="primary"
                                startIcon={<Plus size={16} />}
                                onClick={() => setCreateTicketDialogOpen(true)}
                                sx={{ borderRadius: '8px' }}
                            >
                                Create Ticket
                            </Button>*/}
                        </Box>
                    </Box>

                    {/* Main tabs */}
                    <Tabs
                        value={tabValue}
                        onChange={handleTabChange}
                        sx={{
                            borderBottom: 1,
                            borderColor: 'divider',
                            mb: 3
                        }}
                    >
                        <Tab label="All Tickets"/>
                        <Tab label="Dashboard"/>
                        <Tab label="Analytics"/>
                    </Tabs>

                    {/* Main content based on selected tab */}
                    {loading ? (
                        <Box sx={{display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4}}>
                            <CircularProgress size={40}/>
                        </Box>
                    ) : (
                        <>
                            <TabPanel value={tabValue} index={0}>
                                <TicketList
                                    tickets={tickets}
                                    selectedTickets={selectedTickets}
                                    toggleTicketSelection={toggleTicketSelection}
                                    toggleSelectAll={toggleSelectAll}
                                    openTicketDialog={openTicketDialog}
                                    openBulkActionMenu={openBulkActionMenu}
                                    openScheduleDialog={openScheduleDialog}
                                    showAlert={showAlert}
                                />
                            </TabPanel>

                            <TabPanel value={tabValue} index={1}>
                                <TicketDashboard
                                    tickets={tickets}
                                    openTicketDialog={openTicketDialog}
                                    showAlert={showAlert}
                                />
                            </TabPanel>

                            <TabPanel value={tabValue} index={2}>
                                <TicketAnalytics
                                    tickets={tickets}
                                    showAlert={showAlert}
                                />
                            </TabPanel>
                        </>
                    )}

                    {/* Ticket Detail Dialog */}
                    {selectedTicket && (
                        <TicketDetailDialog
                            open={ticketDialogOpen}
                            ticket={selectedTicket}
                            onClose={closeTicketDialog}
                            onUpdate={updateTicket}
                            staff={serviceProviders} // Pass service providers instead of staff
                            showAlert={showAlert}
                            ApiDetails={ApiDetails}
                        />
                    )}

                    {/* Bulk Action Menu */}
                    <BulkActionMenu
                        anchorEl={bulkActionMenuAnchor}
                        open={Boolean(bulkActionMenuAnchor)}
                        onClose={closeBulkActionMenu}
                        onUpdateStatus={bulkUpdateStatus}
                        onAssignStaff={bulkAssignStaff}
                        onSchedule={openScheduleDialog}
                        staff={serviceProviders} // Pass service providers instead of staff
                    />

                    {/* Schedule Dialog */}
                    <ScheduleDialog
                        open={addScheduleDialogOpen}
                        onClose={closeScheduleDialog}
                        onSchedule={bulkScheduleTickets}
                        staff={serviceProviders} // Pass service providers instead of staff
                        selectedCount={selectedTickets.length}
                        selectedTickets={selectedTickets}
                        ApiDetails={ApiDetails}
                        showAlert={showAlert}
                    />

                    {/* Create Ticket Dialog */}
                    <CreateTicketDialog
                        open={createTicketDialogOpen}
                        onClose={() => setCreateTicketDialogOpen(false)}
                        onTicketCreated={handleCreateTicket}
                        showAlert={showAlert}
                    />

                    {/* Settings Drawer */}
                    <Drawer
                        anchor="right"
                        open={settingsDrawerOpen}
                        onClose={toggleSettingsDrawer}
                        PaperProps={{
                            sx: {
                                width: '80%',
                                maxWidth: '1000px',
                                p: 0
                            }
                        }}
                    >
                        <Box sx={{
                            p: 2,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
                        }}>
                            <Typography variant="h6">Maintenance System Settings</Typography>
                            <IconButton onClick={toggleSettingsDrawer}>
                                <X size={20}/>
                            </IconButton>
                        </Box>

                        <Box sx={{display: 'flex', height: 'calc(100% - 64px)'}}>
                            {/* Settings sidebar */}
                            <Box sx={{width: 240, borderRight: '1px solid rgba(0, 0, 0, 0.12)', p: 2}}>
                                <List>
                                    <ListItem
                                        button
                                        selected={settingsTabValue === 0}
                                        onClick={() => setSettingsTabValue(0)}
                                        sx={{borderRadius: '8px', mb: 1}}
                                    >
                                        <ListItemIcon><Users size={20}/></ListItemIcon>
                                        <ListItemText primary="Service Providers"/>
                                    </ListItem>
                                    <ListItem
                                        button
                                        selected={settingsTabValue === 1}
                                        onClick={() => setSettingsTabValue(1)}
                                        sx={{borderRadius: '8px', mb: 1}}
                                    >
                                        <ListItemIcon><Clipboard size={20}/></ListItemIcon>
                                        <ListItemText primary="Problem Types"/>
                                    </ListItem>
                                    <ListItem
                                        button
                                        selected={settingsTabValue === 2}
                                        onClick={() => setSettingsTabValue(2)}
                                        sx={{borderRadius: '8px', mb: 1}}
                                    >
                                        <ListItemIcon><Tag size={20}/></ListItemIcon>
                                        <ListItemText primary="Categories"/>
                                    </ListItem>
                                </List>
                                <Divider sx={{my: 2}}/>
                                <Typography variant="caption" color="textSecondary" sx={{px: 2}}>
                                    Configure maintenance system settings including service providers, problem types,
                                    and categories.
                                </Typography>
                            </Box>

                            {/* Settings content */}
                            <Box sx={{flex: 1, p: 3, overflowY: 'auto'}}>
                                <ServiceProviderManagement
                                    getLandlordId={getLandlordId}
                                    showAlert={showAlert}
                                    initialTabValue={settingsTabValue}
                                    onUpdateComplete={handleSettingsUpdate}
                                />
                            </Box>
                        </Box>
                    </Drawer>
                </Box>
            </div>
        </ErrorBoundary>
    );
};

export default TenantIssues;